import React, { useMemo, useRef, useState } from "react";
import { C } from "../../../core/constants";
import { localizedPath } from "../../../core/localePaths";
import { useI18n } from "../../i18n/LocaleProvider";
import { APP_FONT_SIZE } from "../../../core/theme";
import { createPdfFileName, printHtmlAsPdf } from "../../export/pdfUtils";
import { buildTarotReadingPdfHtml } from "../../export/tarotReadingPdf";
import { createTarotGeneratePrediction, fetchPredictionById } from "../../predictions/api";
import { toPredictionAccessPopup } from "../../predictions/accessError";
import { getFreeQuotaPopupCopy, shouldShowFreeQuotaPopup } from "../../predictions/freeQuota";
import { applyPredictionUserUpdate } from "../../predictions/postSuccessUserUpdate";
import { TAROT_SPREADS, createTarotReading, hydrateTarotReadingFromPayload } from "../tarotEngine";
import { getTarotCardBackSvg, getTarotCardFaceSvg } from "../art";
import Button from "../../../ui/Button";
import FreeQuotaConfirmPopup from "../../../ui/FreeQuotaConfirmPopup";
import GlassCard from "../../../ui/GlassCard";
import PredictionAccessPopup from "../../../ui/PredictionAccessPopup";
import cardStyles from "./TarotCard.module.css";

let tarotSessionCache = null;
let tarotSpreadSelectionCache = "three_card";
const TAROT_SPREAD_STORAGE_KEY = "ecliptica.tarot.selectedSpread";
// Desktop Celtic grid minimum footprint:
// columns(1008) + gaps(192) + horizontal padding/border buffer(~36+) => ~1236px.
// Add page-level side padding / safety buffer so we switch early before clipping.
const CELTIC_DESKTOP_MIN_WIDTH = 1320;

function getInitialSpreadSelection() {
  if (typeof window !== "undefined") {
    const persisted = window.localStorage.getItem(TAROT_SPREAD_STORAGE_KEY);
    if (persisted && TAROT_SPREADS[persisted]) {
      tarotSpreadSelectionCache = persisted;
      return persisted;
    }
  }
  return TAROT_SPREADS[tarotSpreadSelectionCache] ? tarotSpreadSelectionCache : "three_card";
}

function TarotCardView({
  card,
  isFlipped,
  readingSessionStarted,
  accentColor,
  width,
  height,
  hintLabel,
  onClick,
}) {
  const wrapClassName = [
    cardStyles.wrap,
    isFlipped ? cardStyles.flipped : null,
    !readingSessionStarted ? cardStyles.disabled : null,
  ].filter(Boolean).join(" ");
  const faceClassName = [
    cardStyles.face,
    card?.reversed ? cardStyles.reversed : null,
  ].filter(Boolean).join(" ");
  const backSvg = getTarotCardBackSvg();
  const faceSvg = card?.arcana ? getTarotCardFaceSvg(card) : backSvg;
  return (
    <div
      onClick={onClick}
      className={wrapClassName}
      style={{
        width,
        height,
        boxShadow: `0 8px 18px ${accentColor}22`,
        borderRadius: 12,
      }}
    >
      <div className={cardStyles.inner}>
        <div className={cardStyles.back}>
          <div className={cardStyles.svgFill} dangerouslySetInnerHTML={{ __html: backSvg }} />
          {hintLabel ? <div className={cardStyles.hint}>{hintLabel}</div> : null}
        </div>
        <div className={faceClassName}>
          <div className={cardStyles.svgFill} dangerouslySetInnerHTML={{ __html: faceSvg }} />
        </div>
      </div>
    </div>
  );
}

function GoldText({ children, accent }) {
  return <span style={{ color: accent, textShadow: `0 0 18px ${accent}66` }}>{children}</span>;
}

export default function TarotReadingPage({ acc, t, replayPredictionId, authUser, onCreditsUpdate, onResetPage }) {
  const { locale } = useI18n();
  const fs = APP_FONT_SIZE;
  const layoutContainerRef = useRef(null);
  const interpolate = React.useCallback((template, vars = {}) => {
    return Object.entries(vars).reduce((text, [key, value]) => {
      return text.replace(new RegExp(`%\\{${key}\\}`, "g"), String(value ?? ""));
    }, template);
  }, []);
  const tarotCreditCostRaw = Number(authUser?.predictionCosts?.tarot);
  const tarotCreditCost = Number.isFinite(tarotCreditCostRaw) && tarotCreditCostRaw > 0 ? tarotCreditCostRaw : null;
  const startReadingLabel = tarotCreditCost
    ? interpolate(t("tarot_start_reading_session_cost_dynamic"), { credits: tarotCreditCost })
    : t("tarot_start_reading_session_cost");
  const startAnotherLabel = tarotCreditCost
    ? interpolate(t("tarot_start_another_reading_cost_dynamic"), { credits: tarotCreditCost })
    : t("tarot_start_another_reading_cost");
  const spreadOptions = useMemo(() => ([
    { key: "three_card", label: t("tarot_spread_three_card") },
    { key: "celtic_cross", label: t("tarot_spread_celtic_cross") },
  ]), [t]);
  const [selectedSpreadKey, setSelectedSpreadKey] = useState(() => getInitialSpreadSelection());
  const positionLabels = useMemo(() => ({
    past: t("tarot_pos_past"),
    present: t("tarot_pos_present"),
    future: t("tarot_pos_future"),
    challenge: t("tarot_pos_challenge"),
    foundation: t("tarot_pos_foundation"),
    distant_past: t("tarot_pos_distant_past"),
    recent_past: t("tarot_pos_recent_past"),
    crown: t("tarot_pos_crown"),
    near_future: t("tarot_pos_near_future"),
    self: t("tarot_pos_self"),
    environment: t("tarot_pos_environment"),
    hopes_fears: t("tarot_pos_hopes_fears"),
    outcome: t("tarot_pos_outcome"),
  }), [t]);
  const [reading, setReading] = useState(null);
  const [flipped, setFlipped] = useState([]);
  const [revealed, setRevealed] = useState(false);
  const [readingSessionStarted, setReadingSessionStarted] = useState(false);
  const [loadingReplay, setLoadingReplay] = useState(() => Boolean(replayPredictionId));
  const [showFreeQuotaPopup, setShowFreeQuotaPopup] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [pendingPredictionResponse, setPendingPredictionResponse] = useState(null);
  const [showAccessPopup, setShowAccessPopup] = useState(false);
  const [accessPopupCopy, setAccessPopupCopy] = useState({ title: "", message: "" });
  const [isGenerateLockedByEntitlement, setIsGenerateLockedByEntitlement] = useState(false);
  const [detailModal, setDetailModal] = useState(null);
  const hasRestoredCacheRef = useRef(false);
  const [isCompactLayout, setIsCompactLayout] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < CELTIC_DESKTOP_MIN_WIDTH;
  });
  const [layoutContainerWidth, setLayoutContainerWidth] = useState(() => {
    if (typeof window === "undefined") return 0;
    return window.innerWidth;
  });
  const isReplayMode = Boolean(replayPredictionId);
  const cards = reading?.cards || [];
  const previewCards = useMemo(() => {
    if (cards.length) return cards;
    const positions = TAROT_SPREADS[selectedSpreadKey] || TAROT_SPREADS.three_card;
    return positions.map((positionKey, idx) => ({
      cardId: `preview-${positionKey}-${idx}`,
      positionKey,
      positionLabel: positionLabels[positionKey] || positionKey,
      reversed: false,
      number: "",
      icon: "✦",
      name: "",
      meaning: "",
    }));
  }, [cards, selectedSpreadKey, positionLabels]);
  const synthesisText = reading?.synthesis || t("tarot_synthesis_text");
  const showReplayLoadingPanel = isReplayMode && loadingReplay && !reading;
  const isCelticCross = selectedSpreadKey === "celtic_cross";
  const isDesktopCeltic = isCelticCross && !isCompactLayout;
  // Keep card size consistent between 3-card and celtic-cross layouts.
  // This also matches the native SVG ratio (160x272) to avoid resampling blur.
  const cardWidth = 160;
  const cardHeight = 272;
  const celticDesktopSlotMeta = useMemo(() => ({
    crown: { area: "crown" },
    foundation: { area: "foundation" },
    distant_past: { area: "foundation" },
    present: { area: "center", zIndex: 1 },
    challenge: { area: "center", zIndex: 2, horizontal: true },
    recent_past: { area: "recent" },
    near_future: { area: "near" },
    self: { area: "self" },
    environment: { area: "environment" },
    hopes_fears: { area: "hopes" },
    outcome: { area: "outcome" },
  }), []);
  const getCardColor = (index) => {
    const palette = [acc.primary, acc.secondary, acc.tertiary];
    return palette[index % palette.length];
  };
  const celticPositionStyle = (positionKey) => {
    const slot = celticDesktopSlotMeta[positionKey];
    if (!slot?.area) return {};
    return { gridArea: slot.area };
  };
  const cardSlotStyle = (card) => {
    if (!isDesktopCeltic) return {};
    const base = celticPositionStyle(card.positionKey);
    const slotMeta = celticDesktopSlotMeta[card.positionKey] || {};
    return {
      ...base,
      position: "relative",
      justifySelf: "center",
      alignSelf: "center",
      zIndex: slotMeta.zIndex || 1,
    };
  };
  const activeDetailCard = detailModal ? cards[detailModal.index] : null;
  const detailCardColor = activeDetailCard ? getCardColor(detailModal.index) : acc.primary;

  const setSelectedSpreadKeyWithPersistence = React.useCallback((nextKey) => {
    if (!TAROT_SPREADS[nextKey]) return;
    tarotSpreadSelectionCache = nextKey;
    if (typeof window !== "undefined") {
      window.localStorage.setItem(TAROT_SPREAD_STORAGE_KEY, nextKey);
    }
    setSelectedSpreadKey(nextKey);
  }, []);

  function toReadingPayload(sourceReading) {
    if (!sourceReading?.cards?.length) return null;
    return {
      spread_key: sourceReading.spreadKey || selectedSpreadKey,
      seed: sourceReading.seed || null,
      synthesis: sourceReading.synthesis || null,
      cards: sourceReading.cards.map((card) => ({
        card_id: card.cardId,
        position_key: card.positionKey,
        position_label: card.positionLabel,
        reversed: card.reversed,
        number: card.number,
        icon: card.icon,
        name: card.name,
        meaning: card.meaning,
        arcana: card.arcana,
        suit: card.suit,
        rank_key: card.rankKey,
      })),
    };
  }

  React.useEffect(() => {
    const node = layoutContainerRef.current;
    if (!node) return undefined;

    function syncCompactLayout(nextWidth) {
      const effectiveWidth = Number(nextWidth) || (typeof window !== "undefined" ? window.innerWidth : 0);
      setLayoutContainerWidth(effectiveWidth);
      setIsCompactLayout(effectiveWidth < CELTIC_DESKTOP_MIN_WIDTH);
    }

    syncCompactLayout(node.getBoundingClientRect().width);

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        syncCompactLayout(entry.contentRect.width);
      });
      observer.observe(node);
      return () => observer.disconnect();
    }

    function handleResize() {
      syncCompactLayout(node.getBoundingClientRect().width);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  React.useEffect(() => {
    if (!replayPredictionId) return;
    let cancelled = false;
    async function loadReplayPrediction() {
      setLoadingReplay(true);
      try {
        const response = await fetchPredictionById(replayPredictionId);
        if (cancelled) return;
        const prediction = response?.prediction;
        if (!prediction || prediction.feature !== "tarot") return;
        const replaySpreadKey = prediction?.result_payload?.spread_key || prediction?.input_payload?.spread_key || "three_card";
        const hydrated = hydrateTarotReadingFromPayload({
          payload: prediction.result_payload || {},
          locale,
          positionLabels,
          t,
        });
        const fallbackReading = createTarotReading({
          locale,
          spreadKey: replaySpreadKey,
          positionLabels,
          seed: `${Date.now()}-replay-fallback`,
          t,
        });
        const nextReading = hydrated || fallbackReading;
        setSelectedSpreadKeyWithPersistence(nextReading.spreadKey || replaySpreadKey);
        setReading(nextReading);
        setDetailModal(null);
        setReadingSessionStarted(true);
        setFlipped(Array.from({ length: nextReading.cards.length }, () => true));
        setRevealed(true);
      } catch (_) {
        // fall back to normal flow
      } finally {
        if (!cancelled) setLoadingReplay(false);
      }
    }
    loadReplayPrediction();
    return () => {
      cancelled = true;
    };
  }, [replayPredictionId, locale, positionLabels, t, setSelectedSpreadKeyWithPersistence]);

  React.useEffect(() => {
    if (isReplayMode || hasRestoredCacheRef.current || !tarotSessionCache?.payload) return;
    const restoredReading = hydrateTarotReadingFromPayload({
      payload: tarotSessionCache.payload,
      locale,
      positionLabels,
      t,
    });
    if (restoredReading) {
      setSelectedSpreadKeyWithPersistence(restoredReading.spreadKey || tarotSessionCache.spreadKey || "three_card");
      setReading(restoredReading);
      setFlipped(Array.isArray(tarotSessionCache.flipped) ? tarotSessionCache.flipped : Array.from({ length: restoredReading.cards.length }, () => false));
      setRevealed(Boolean(tarotSessionCache.revealed));
      setReadingSessionStarted(Boolean(tarotSessionCache.readingSessionStarted));
    }
    hasRestoredCacheRef.current = true;
  }, [isReplayMode, locale, positionLabels, t, setSelectedSpreadKeyWithPersistence]);

  React.useEffect(() => {
    if (isReplayMode || !readingSessionStarted || !reading) return;
    tarotSessionCache = {
      spreadKey: selectedSpreadKey,
      payload: toReadingPayload(reading),
      flipped,
      revealed,
      readingSessionStarted,
    };
  }, [isReplayMode, selectedSpreadKey, reading, flipped, revealed, readingSessionStarted]);

  function flipAll() {
    setFlipped(Array.from({ length: cards.length }, () => true));
    setTimeout(() => setRevealed(true), 600);
  }

  React.useEffect(() => {
    if (isReplayMode || !pendingPredictionResponse || !readingSessionStarted || sessionLoading) return;
    applyPredictionUserUpdate({ authUser, predictionResponse: pendingPredictionResponse, onCreditsUpdate });
    setPendingPredictionResponse(null);
  }, [isReplayMode, pendingPredictionResponse, readingSessionStarted, sessionLoading, authUser, onCreditsUpdate]);

  React.useEffect(() => {
    if (isReplayMode || !isGenerateLockedByEntitlement) return;
    if (!shouldShowFreeQuotaPopup(authUser, "tarot")) {
      setIsGenerateLockedByEntitlement(false);
    }
  }, [authUser, isGenerateLockedByEntitlement, isReplayMode]);

  async function runStartReadingSession() {
    if (sessionLoading || isGenerateLockedByEntitlement) return;
    setSessionLoading(true);
    const generatedReading = createTarotReading({
      locale,
      spreadKey: selectedSpreadKey,
      positionLabels,
      seed: `${Date.now()}-${Math.random()}`,
      t,
    });
    if (!isReplayMode) {
      try {
        const predictionResponse = await createTarotGeneratePrediction({
          input_payload: { locale, spread_key: generatedReading.spreadKey },
          result_payload: {
            spread_key: generatedReading.spreadKey,
            seed: generatedReading.seed,
            cards: generatedReading.cards.map((card) => ({
              card_id: card.cardId,
              position_key: card.positionKey,
              position_label: card.positionLabel,
              reversed: card.reversed,
              number: card.number,
              icon: card.icon,
              name: card.name,
              meaning: card.meaning,
              arcana: card.arcana,
              suit: card.suit,
              rank_key: card.rankKey,
            })),
            synthesis: generatedReading.synthesis,
          },
        });
        setPendingPredictionResponse(predictionResponse);
      } catch (error) {
        const popupPayload = toPredictionAccessPopup(error, t);
        if (popupPayload.shouldShowPopup) {
          setAccessPopupCopy({ title: popupPayload.title, message: popupPayload.message });
          setShowAccessPopup(true);
          setIsGenerateLockedByEntitlement(true);
          return;
        }
        // keep UX responsive if tracking call fails
      } finally {
        setSessionLoading(false);
      }
    } else {
      setSessionLoading(false);
    }
    setIsGenerateLockedByEntitlement(false);
    setReading(generatedReading);
    setDetailModal(null);
    setFlipped(Array.from({ length: generatedReading.cards.length }, () => false));
    setRevealed(false);
    setReadingSessionStarted(true);
  }

  function startReadingSession({ skipFreeQuotaGuard = false } = {}) {
    if (sessionLoading || isGenerateLockedByEntitlement) return;
    if (!isReplayMode && !skipFreeQuotaGuard && shouldShowFreeQuotaPopup(authUser, "tarot")) {
      setShowFreeQuotaPopup(true);
      return;
    }
    runStartReadingSession();
  }

  function exportReadingToPdf() {
    if (!revealed || !cards.length) return;
    const now = new Date();
    const exportFileName = createPdfFileName("tarot-reading", now);
    const html = buildTarotReadingPdfHtml({
      cards,
      synthesisText,
      exportedAt: now.toLocaleString(),
      labels: {
        report: t("pdf_report_title"),
        spreadMeta: reading?.spreadKey === "celtic_cross" ? t("tarot_spread_celtic_cross") : t("tarot_three_card_spread"),
        synthesis: t("pdf_synthesis"),
        generatedBy: t("pdf_generated_by"),
        upright: t("tarot_orientation_upright"),
        reversed: t("tarot_orientation_reversed"),
      }
    });
    printHtmlAsPdf({ html, title: exportFileName.replace(/\.pdf$/i, "") });
  }

  return (
    <div ref={layoutContainerRef} style={{ padding: 32 }}>
      <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 40, fontWeight: 400, color: C.textPrimary, marginBottom: 8 }}>{t("tarot_reading")}</h1>
      <p style={{ color: C.textSecondary, fontSize: fs, marginBottom: 14 }}>
        {selectedSpreadKey === "celtic_cross" ? t("tarot_spread_celtic_cross") : t("tarot_three_card_spread")}
      </p>
      {!isReplayMode && (
        <div style={{ marginBottom: 24, display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ fontSize: 12, color: C.textMuted, letterSpacing: "0.08em", textTransform: "uppercase" }}>{t("tarot_spread_label")}</div>
          <select
            value={selectedSpreadKey}
            onChange={(e) => setSelectedSpreadKeyWithPersistence(e.target.value)}
            disabled={sessionLoading || readingSessionStarted}
            style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 10, color: C.textPrimary, padding: "8px 12px", fontSize: fs - 1, outline: "none" }}
          >
            {spreadOptions.map((option) => (
              <option key={option.key} value={option.key} style={{ background: "#0c1230", color: C.textPrimary }}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
        {!readingSessionStarted && !isReplayMode && (
          <Button variant="primary" acc={acc} onClick={startReadingSession} disabled={sessionLoading || isGenerateLockedByEntitlement}>
            {sessionLoading ? t("tarot_loading_session") : startReadingLabel}
          </Button>
        )}
        {readingSessionStarted && cards.length > 0 && !flipped.every(Boolean) && !isReplayMode && (
          <Button variant="primary" acc={acc} onClick={flipAll} disabled={sessionLoading}>
            {t("tarot_reveal_all_cards")}
          </Button>
        )}
        {readingSessionStarted && !isReplayMode && (
          <Button variant="outline" acc={acc} onClick={startReadingSession} disabled={sessionLoading || isGenerateLockedByEntitlement}>
            {sessionLoading ? t("tarot_loading_session") : startAnotherLabel}
          </Button>
        )}
        {readingSessionStarted && !isReplayMode && (
          <Button
            variant="outline"
            acc={acc}
            onClick={() => {
              setReadingSessionStarted(false);
              setReading(null);
              setFlipped([]);
              setRevealed(false);
              tarotSessionCache = null;
            }}
          >
            {t("tarot_change_spread")}
          </Button>
        )}
        {readingSessionStarted && !isReplayMode && !sessionLoading && (
          <div style={{ fontSize: 13, color: C.textMuted, alignSelf: "center" }}>
            <span style={{ color: acc.primary }}>✦</span> {interpolate(t("tarot_credits_used_dynamic"), { credits: tarotCreditCost || 4 })}
          </div>
        )}
        {isReplayMode && (
          <div style={{ color: C.textMuted, fontSize: 13 }}>
            <span style={{ color: acc.primary }}>✦</span> {loadingReplay ? t("tarot_loading_replay") : t("dashboard_replay_mode_badge")}
          </div>
        )}
        {isReplayMode && !loadingReplay && (
          <Button
            variant="primary"
            acc={acc}
            onClick={() => (onResetPage ? onResetPage() : window.location.assign(localizedPath("tarot", locale)))}
          >
            {t("dashboard_replay_start_new_tarot")}
          </Button>
        )}
      </div>
      {showReplayLoadingPanel ? (
        <GlassCard style={{ padding: 28, marginBottom: 48 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: acc.primary, boxShadow: `0 0 12px ${acc.primary}` }} />
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 24, color: C.textPrimary }}>{t("tarot_loading_replay")}</div>
          </div>
          <div style={{ fontSize: 13, color: C.textSecondary, marginBottom: 14 }}>{t("tarot_loading_replay")}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 12 }}>
            {Array.from({ length: selectedSpreadKey === "celtic_cross" ? 10 : 3 }).map((_, i) => (
              <div
                key={`tarot-replay-loading-${i}`}
                style={{
                  height: 180,
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.10)",
                  background: "linear-gradient(90deg, rgba(255,255,255,0.03), rgba(255,255,255,0.08), rgba(255,255,255,0.03))",
                }}
              />
            ))}
          </div>
        </GlassCard>
      ) : (
        <div
          style={
            isDesktopCeltic
              ? {
                  marginBottom: 48,
                  perspective: "1000px",
                  width: "100%",
                  maxWidth: 1280,
                  minHeight: 700,
                  marginInline: "auto",
                  padding: "20px 18px",
                  boxSizing: "border-box",
                  borderRadius: 16,
                  border: `1px solid ${acc.primary}33`,
                  background: `linear-gradient(135deg, ${acc.primary}14, ${acc.tertiary}10)`,
                  display: "grid",
                  // Symmetric recent/near lanes around a dedicated center lane.
                  // Sized to fit safely inside maxWidth with gaps/padding.
                  gridTemplateColumns: "minmax(64px,1fr) minmax(160px,1.15fr) minmax(280px,1.6fr) minmax(160px,1.15fr) minmax(120px,0.9fr) minmax(160px,1.15fr) minmax(64px,1fr)",
                  gridTemplateRows: "repeat(4, minmax(180px, auto))",
                  gridTemplateAreas: `
                    ". . crown . . outcome ."
                    ". recent center near . hopes ."
                    ". . foundation . . environment ."
                    ". . . . . self ."
                  `,
                  columnGap: 32,
                  rowGap: 40,
                  alignItems: "center",
                  justifyItems: "center",
                }
              : { display: "flex", justifyContent: "center", gap: 24, flexWrap: "wrap", marginBottom: 48, perspective: "1000px" }
          }
        >
          {previewCards.map((card, i) => {
            const accentColor = getCardColor(i);
            const isFlipped = flipped[i];
            const slotMeta = isDesktopCeltic ? (celticDesktopSlotMeta[card.positionKey] || {}) : {};
            const isHorizontalSlot = Boolean(slotMeta.horizontal);
            const slotCardWidth = isHorizontalSlot ? cardHeight : cardWidth;
            const slotCardHeight = isHorizontalSlot ? cardWidth : cardHeight;
            const slotLabelStyle = isHorizontalSlot
              ? { position: "absolute", top: "50%", left: "calc(100% + 12px)", transform: "translateY(-50%)", fontSize: 12, color: C.textMuted, letterSpacing: "0.15em", textTransform: "uppercase", whiteSpace: "nowrap", textAlign: "left", pointerEvents: "none" }
              : { fontSize: 12, color: C.textMuted, letterSpacing: "0.15em", textTransform: "uppercase" };
            const hintLabel = readingSessionStarted ? t("tarot_click_to_reveal") : t("tarot_start_session_first");
            return (
              <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, ...cardSlotStyle(card) }}>
                <div style={slotLabelStyle}>{card.positionLabel}</div>
                <TarotCardView
                  card={card}
                  isFlipped={isFlipped}
                  readingSessionStarted={readingSessionStarted}
                  accentColor={accentColor}
                  width={slotCardWidth}
                  height={slotCardHeight}
                  hintLabel={hintLabel}
                  onClick={() => {
                    if (!readingSessionStarted) return;
                    if (isFlipped && revealed) {
                      setDetailModal({ index: i });
                      return;
                    }
                    const nf = [...flipped];
                    nf[i] = true;
                    setFlipped(nf);
                    if (nf.every(Boolean)) setTimeout(() => setRevealed(true), 400);
                  }}
                />
              </div>
            );
          })}
        </div>
      )}
      {revealed && (
        <GlassCard style={{ padding: 36, maxWidth: 800, margin: "0 auto" }} glow accent={acc.primary}>
          <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 30, fontWeight: 400, color: C.textPrimary, marginBottom: 24, textAlign: "center" }}>{t("tarot_your")} <GoldText accent={acc.secondary}>{t("tarot_reading_label")}</GoldText></h2>
          <div style={{ fontSize: 12, color: C.textMuted, textAlign: "center", marginBottom: 16 }}>{t("tarot_result_click_card_hint")}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 24 }}>
            {cards.map((card, i) => (
              <div key={i} style={{ border: "1px solid rgba(255,255,255,0.10)", borderRadius: 12, padding: "14px 14px 12px", background: "rgba(255,255,255,0.02)" }}>
                <div style={{ fontSize: 11, color: getCardColor(i), letterSpacing: "0.11em", marginBottom: 6, textTransform: "uppercase" }}>{String(card.positionLabel).toUpperCase()}</div>
                <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 24, color: C.textPrimary, marginBottom: 8 }}>{card.name}</div>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, color: card.reversed ? "#f5b3c4" : "#9cd8d3", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 999, padding: "3px 9px", marginBottom: 10 }}>
                  <span>{t("tarot_result_orientation_heading")}:</span>
                  <strong>{card.reversed ? t("tarot_orientation_reversed") : t("tarot_orientation_upright")}</strong>
                </div>
                <div style={{ fontSize: 12, color: C.textMuted, lineHeight: 1.55, marginBottom: 10 }}>
                  {card.reversed ? t("tarot_result_orientation_reversed_explainer") : t("tarot_result_orientation_upright_explainer")}
                </div>
                <p style={{ fontSize: fs - 1, color: C.textSecondary, lineHeight: 1.7, margin: 0 }}>{card.meaning}</p>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 20, display: "flex", justifyContent: "center" }}>
            <Button variant="outline" acc={acc} onClick={exportReadingToPdf}>{t("export_result_pdf")}</Button>
          </div>
        </GlassCard>
      )}
      {activeDetailCard && (
        <div
          onClick={() => setDetailModal(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(5,10,25,0.72)",
            backdropFilter: "blur(4px)",
            zIndex: 60,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(640px, 92vw)",
              maxHeight: "88vh",
              overflow: "auto",
              borderRadius: 16,
              background: "linear-gradient(150deg, rgba(8,14,36,0.98), rgba(9,16,40,0.98))",
              border: `1px solid ${detailCardColor}66`,
              boxShadow: `0 20px 60px rgba(0,0,0,0.45), 0 0 28px ${detailCardColor}44`,
              padding: 22,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 12, marginBottom: 14 }}>
              <div>
                <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 32, color: C.textPrimary }}>{activeDetailCard.name}</div>
                <div style={{ fontSize: 12, color: detailCardColor, letterSpacing: "0.08em", textTransform: "uppercase", marginTop: 4 }}>
                  {activeDetailCard.positionLabel} · {activeDetailCard.reversed ? t("tarot_detail_reversed") : t("tarot_detail_upright")}
                </div>
              </div>
              <button type="button" onClick={() => setDetailModal(null)} style={{ border: "none", background: "transparent", color: C.textMuted, fontSize: 24, cursor: "pointer", padding: 0 }}>×</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 10, marginBottom: 14 }}>
              <div style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "10px 12px", background: "rgba(255,255,255,0.02)" }}>
                <div style={{ fontSize: 11, color: C.textMuted, letterSpacing: "0.08em", textTransform: "uppercase" }}>{t("tarot_detail_position_label")}</div>
                <div style={{ fontSize: 13, color: C.textPrimary, marginTop: 4 }}>{activeDetailCard.positionLabel}</div>
              </div>
              <div style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "10px 12px", background: "rgba(255,255,255,0.02)" }}>
                <div style={{ fontSize: 11, color: C.textMuted, letterSpacing: "0.08em", textTransform: "uppercase" }}>{t("tarot_detail_arcana_label")}</div>
                <div style={{ fontSize: 13, color: C.textPrimary, marginTop: 4 }}>
                  {activeDetailCard.arcana === "major" ? t("tarot_detail_arcana_major") : t("tarot_detail_arcana_minor")}
                </div>
              </div>
              {activeDetailCard.arcana === "minor" && (
                <div style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "10px 12px", background: "rgba(255,255,255,0.02)" }}>
                  <div style={{ fontSize: 11, color: C.textMuted, letterSpacing: "0.08em", textTransform: "uppercase" }}>{t("tarot_detail_suit_label")}</div>
                  <div style={{ fontSize: 13, color: C.textPrimary, marginTop: 4 }}>{t(`tarot_minor_suit_${activeDetailCard.suit}`)}</div>
                </div>
              )}
            </div>
            <div style={{ border: `1px solid ${detailCardColor}33`, borderRadius: 12, padding: "12px 14px", background: `${detailCardColor}10`, marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: detailCardColor, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>
                {t("tarot_detail_meaning_label")}
              </div>
              <div style={{ fontSize: 14, color: C.textSecondary, lineHeight: 1.65 }}>{activeDetailCard.meaning}</div>
            </div>
            <div style={{ border: "1px solid rgba(255,255,255,0.10)", borderRadius: 12, padding: "12px 14px", background: "rgba(255,255,255,0.02)" }}>
              <div style={{ fontSize: 11, color: C.textMuted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>
                {t("tarot_detail_practical_tip_label")}
              </div>
              <div style={{ fontSize: 13, color: C.textSecondary, lineHeight: 1.6 }}>
                {interpolate(
                  activeDetailCard.reversed ? t("tarot_detail_practical_tip_reversed") : t("tarot_detail_practical_tip_upright"),
                  { position: activeDetailCard.positionLabel.toLowerCase() },
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <FreeQuotaConfirmPopup
        open={showFreeQuotaPopup}
        title={getFreeQuotaPopupCopy("tarot", t).title}
        message={getFreeQuotaPopupCopy("tarot", t).message}
        note={getFreeQuotaPopupCopy("tarot", t).note}
        confirmLabel={t("free_quota_popup_confirm")}
        cancelLabel={t("free_quota_popup_cancel")}
        onClose={() => setShowFreeQuotaPopup(false)}
        onConfirm={() => {
          setShowFreeQuotaPopup(false);
          startReadingSession({ skipFreeQuotaGuard: true });
        }}
        acc={acc}
      />
      <PredictionAccessPopup
        open={showAccessPopup}
        title={accessPopupCopy.title}
        message={accessPopupCopy.message}
        onClose={() => setShowAccessPopup(false)}
        onGoToPricing={() => {
          window.location.href = localizedPath("creditsPlans", locale);
        }}
        acc={acc}
      />
    </div>
  );
}
